import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import rankingService from './rankingService';

/**
 * Pull a message out of a failed request without assuming a body shape.
 *
 * The auth middleware answers 401/403 with plain text rather than JSON, and a
 * dropped connection has no `response` at all - both of which would throw on the
 * unguarded `error.response.data` used elsewhere in the app.
 */
const extractError = (error) => {
  const data = error?.response?.data;

  if (typeof data === 'string' && data) {
    return { message: data, errors: [] };
  }

  if (data?.message) {
    return { message: data.message, errors: data.errors || [] };
  }

  return { message: error?.message || 'Something went wrong.', errors: [] };
};

export const getRankingLookup = createAsyncThunk(
  'ranking/getRankingLookup',
  async (_, thunkAPI) => {
    return await rankingService
      .getRankingLookup()
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(extractError(error));
      });
  }
);

export const getRankingPositions = createAsyncThunk(
  'ranking/getRankingPositions',
  async (category, thunkAPI) => {
    return await rankingService
      .getRankingPositions(category)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(extractError(error));
      });
  }
);

export const saveRankingMapping = createAsyncThunk(
  'ranking/saveRankingMapping',
  async ({ eventId, sectionMapping }, thunkAPI) => {
    return await rankingService
      .saveRankingMapping(eventId, sectionMapping)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(extractError(error));
      });
  }
);

export const exportRankingPositions = createAsyncThunk(
  'ranking/exportRankingPositions',
  async (data, thunkAPI) => {
    return await rankingService
      .exportRankingPositions(data.eventId, data)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(extractError(error));
      });
  }
);

export const rankingSlice = createSlice({
  name: 'ranking',
  initialState: {
    lookup: null,
    // Positions are per category, so the list in state belongs to whichever
    // category it was fetched for - the dialog must not show one category's
    // positions while another is selected.
    positions: null,
    positionsCategory: null,
    // What the server stored last time the dialog saved a classification. Only
    // for confirmation - the dialog reads the mapping from the event.
    savedMapping: null,
    exportResult: null
  },
  reducers: {
    clearExportResult: (state) => {
      state.exportResult = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRankingLookup.fulfilled, (state, action) => {
        state.lookup = action.payload;
        state.loadingLookup = false;
      })
      .addCase(getRankingLookup.rejected, (state) => {
        state.loadingLookup = false;
      })
      .addCase(getRankingLookup.pending, (state) => {
        state.loadingLookup = true;
      })
      .addCase(getRankingPositions.fulfilled, (state, action) => {
        state.positions = action.payload.positions || [];
        state.positionsCategory = action.meta.arg;
      })
      .addCase(getRankingPositions.rejected, (state) => {
        state.positions = null;
        state.positionsCategory = null;
      })
      .addCase(saveRankingMapping.fulfilled, (state, action) => {
        state.savedMapping = action.payload.rankingExport;
      })
      .addCase(exportRankingPositions.fulfilled, (state, action) => {
        state.exportResult = action.payload;
        state.exporting = false;
      })
      .addCase(exportRankingPositions.rejected, (state) => {
        state.exporting = false;
      })
      .addCase(exportRankingPositions.pending, (state) => {
        state.exporting = true;
      });
  }
});

export const { clearExportResult } = rankingSlice.actions;

export default rankingSlice.reducer;
